import { Injectable } from '@angular/core';
import { RangeEstimationRow, RangeEstimationsService } from './range-estimations.service';
import { RangeAndDeltaRow, RangeAndDeltaService } from './range-and-delta.service';
import { NextDayBiasService } from './next-day-bias.service';
import { EconEventsIndexService } from './econ-events-index.service';
import { FirebaseService } from './firebase.service';
import { collection, getDocs, query, where } from 'firebase/firestore';

export type RangeType = 'today' | 'yesterday' | 'bias' | 'econ' | 'holiday';

export const TICKERS = ['NQ', 'ES', 'RTY', 'GC', 'QQQ', 'SPY', 'IWM'];

export const TIME_OPTIONS: { id: RangeType; label: string }[] = [
  { id: 'today',     label: "Today's Range" },
  { id: 'yesterday', label: "Yesterday's Range" },
  { id: 'bias',      label: 'Next Day Bias' },
  { id: 'econ',      label: 'Economic Events' },
  { id: 'holiday',   label: 'Holiday Movement' },
];

@Injectable({ providedIn: 'root' })
export class ChatbotService {
  constructor(
    private rangeEstimations: RangeEstimationsService,
    private rangeAndDelta: RangeAndDeltaService,
    private nextDayBias: NextDayBiasService,
    private econEventsIndex: EconEventsIndexService,
    private firebase: FirebaseService,
  ) {}

  /** Released "actual" and "previous" values (e.g. 3.1%) per date for one event, from the public economicCalendar. */
  private async loadEconActuals(eventName: string): Promise<Map<string, { actual: string; previous: string }>> {
    const actuals = new Map<string, { actual: string; previous: string }>();
    try {
      const snap = await getDocs(query(collection(this.firebase.db, 'economicCalendar'), where('economicEvent', '==', eventName)));
      for (const d of snap.docs) {
        const r = d.data() as { date?: string; actual?: string; previous?: string };
        if (r.date) actuals.set(r.date, { actual: r.actual?.trim() ?? '', previous: r.previous?.trim() ?? '' });
      }
    } catch (err) {
      console.error('Chatbot: could not load event actuals for', eventName, err);
    }
    return actuals;
  }

  async answerRange(ticker: string, rangeType: RangeType, date?: string): Promise<string> {
    if (rangeType === 'today') {
      // Read from stored Firestore snapshot — 1 read, same values as Range & Delta page
      const row = await this.rangeAndDelta.getStoredForecast(ticker);
      if (!row) return `No forecast data saved for ${ticker} yet. Ask an admin to open the Range & Delta page to generate it.`;
      return this.formatForecast(row);
    }

    if (rangeType === 'bias') {
      const bias = await this.nextDayBias.get(ticker);
      if (!bias) return `No Next Day Bias saved for ${ticker} yet. Ask an admin to open the Next Day Bias page to generate it.`;
      return this.formatBias(bias);
    }

    let row: RangeEstimationRow | undefined;

    if (rangeType === 'yesterday') {
      const yesterday = this.rangeAndDelta.previousTradingDate(new Date());
      const [histRows, forecast] = await Promise.all([
        this.rangeEstimations.loadRowsByTickerDates('daily', [{ ticker, date: yesterday }]),
        this.rangeAndDelta.getHistoricalForecast(ticker, yesterday).catch(() => null),
      ]);
      row = histRows[0];
      if (row) return this.formatHistoricalWithForecast(row, forecast);
    }

    if (!row) {
      return `No range data found for ${ticker} for yesterday. Data may not have been entered for that session.`;
    }

    return this.formatHistorical(row);
  }

  async loadHolidays(): Promise<{ date: string; name: string }[]> {
    const all = await this.rangeAndDelta.getHolidays();
    const today = new Date().toISOString().slice(0, 10);
    return all.filter(h => h.date < today); // all past holidays
  }

  async answerHoliday(
    ticker: string,
    allHolidays: { date: string; name: string }[],
    selectedNames: string[],
    periods: string[],
  ): Promise<string> {
    const today = new Date().toISOString().slice(0, 10);
    const fmt = (v: number | undefined | null) => v != null ? v.toFixed(2) : '—';

    // Collect all dates we need to fetch
    const datesToFetch = new Set<string>();
    const plan: { sectionName: string; dates: string[] }[] = [];

    for (const name of selectedNames) {
      const occurrences = allHolidays.filter(h => h.name === name && h.date < today);
      for (const period of periods) {
        const sectionDates: string[] = [];
        for (const h of occurrences) {
          if (period === 'week-before') {
            const wk = this.rangeAndDelta.weekBeforeDates(h.date);
            wk.forEach(d => { datesToFetch.add(d); sectionDates.push(d); });
          } else if (period === 'day-before') {
            const d = this.rangeAndDelta.previousTradingDate(new Date(`${h.date}T00:00:00`));
            datesToFetch.add(d); sectionDates.push(d);
          } else if (period === 'day-after') {
            const d = this.rangeAndDelta.nextTradingDate(h.date);
            datesToFetch.add(d); sectionDates.push(d);
          }
        }
        const periodLabel = period === 'week-before' ? 'Week Before' : period === 'day-before' ? 'Day Before' : 'Day After';
        plan.push({ sectionName: `${name} — ${periodLabel}`, dates: sectionDates });
      }
    }

    const rows = await this.rangeEstimations.loadRowsByTickerDates(
      'daily', [...datesToFetch].map(date => ({ ticker, date }))
    );
    const byDate = new Map(rows.map(r => [r.date, r]));

    const sections = plan.map(({ sectionName, dates }) => ({
      name: sectionName,
      rows: dates
        .map(date => { const r = byDate.get(date); return r ? [date, fmt(r.range), fmt(r.high), fmt(r.low), fmt(r.currentClose)] : null; })
        .filter((r): r is string[] => r !== null),
    }));

    const payload = {
      title: `${ticker} — Holiday Market Movement`,
      headers: ['Date', 'Range', 'High', 'Low', 'Close'],
      sections,
    };
    return `TABLE:${JSON.stringify(payload)}`;
  }

  async loadEconEventNames(): Promise<string[]> {
    const index = await this.econEventsIndex.get();
    if (!index) return [];
    // Only return event names that have at least one past date with actual data
    const today = new Date().toISOString().slice(0, 10);
    return Object.entries(index.events)
      .filter(([, dates]) => dates.some(d => d < today))
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name]) => name);
  }

  async answerEconEvents(ticker: string, eventNames: string[]): Promise<string> {
    const index = await this.econEventsIndex.get();
    if (!index) return 'Economic events index not available yet. Ask an admin to open the Economic Calendar page to generate it.';

    const today = new Date().toISOString().slice(0, 10);
    const dateLookups: { ticker: string; date: string; eventName: string }[] = [];

    for (const name of eventNames) {
      const dates = (index.events[name] ?? []).filter(d => d < today);
      for (const date of dates) {
        dateLookups.push({ ticker, date, eventName: name });
      }
    }

    if (!dateLookups.length) return 'No past dates found for the selected events.';

    const rows = await this.rangeEstimations.loadRowsByTickerDates(
      'daily',
      dateLookups.map(d => ({ ticker: d.ticker, date: d.date })),
    );

    const rowByDate = new Map(rows.map(r => [r.date, r]));
    const fmt = (v: number | undefined | null) => v != null ? v.toFixed(2) : 'N/A';

    const sections: { name: string; rows: string[][] }[] = [];

    const actualsByEvent = await Promise.all(eventNames.map(n => this.loadEconActuals(n)));

    for (const [i, name] of eventNames.entries()) {
      const dates = (index.events[name] ?? []).filter(d => d < today).slice().reverse();
      const dataRows: string[][] = [];
      for (const date of dates) {
        const row = rowByDate.get(date);
        if (!row) continue;
        const econ = actualsByEvent[i].get(date);
        dataRows.push([date, econ?.actual || '—', econ?.previous || '—', fmt(row.range), fmt(row.high), fmt(row.low), fmt(row.currentClose)]);
      }
      sections.push({ name, rows: dataRows });
    }

    const payload = {
      title: `${ticker} — Economic Event Ranges`,
      headers: ['Date', 'Actual', 'Previous', 'Range', 'High', 'Low', 'Close'],
      sections,
    };
    return `TABLE:${JSON.stringify(payload)}`;
  }

  private formatBias(bias: import('./next-day-bias.service').NextDayBiasDoc): string {
    const direction = (d: string) => d === 'bull' ? '▲' : d === 'bear' ? '▼' : '●';
    const lines = [
      `${bias.ticker} — Next Day Bias`,
      `Signal : ${bias.signal}   Score : ${bias.score}`,
      ``,
      `── Factors ──`,
      ...bias.factors
      .filter(f => f.label !== 'IV Level' && f.label !== 'IV Change')
      .map(f => `${direction(f.direction)} ${f.label.padEnd(16)} ${f.value}`),
      ``,
      `ℹ  Based on : ${bias.basedOnDate}`,
    ];
    return lines.join('\n');
  }

  private formatForecast(row: RangeAndDeltaRow): string {
    const f = (v: number | null) => this.rangeAndDelta.formatNumber(v);
    return [
      `${row.ticker} Forecast — ${row.forecastFor}`,
      `Based on : ${row.basedOnDate}`,
      ``,
      `Forecast Range : ${f(row.forecastRange)}`,
    ].join('\n');
  }

  private formatHistoricalWithForecast(row: RangeEstimationRow, forecast: RangeAndDeltaRow | null): string {
    const fmt = (v: number | undefined | null) => v != null ? v.toFixed(2) : 'N/A';
    const f = (v: number | null) => this.rangeAndDelta.formatNumber(v);
    const diff = (forecastVal: number | null): string => {
      if (forecastVal == null || row.range == null) return '';
      const d = forecastVal - row.range;
      return d >= 0 ? `  +${d.toFixed(2)}` : `  ${d.toFixed(2)}`;
    };

    const lines = [
      `${row.ticker} — ${row.date} (Actual)`,
      `Range : ${fmt(row.range)}`,
      `High  : ${fmt(row.high)}`,
      `Low   : ${fmt(row.low)}`,
      `Close : ${fmt(row.currentClose)}`,
    ];

    if (forecast) {
      const matchDate = forecast.forecastFor === row.date;
      const header = matchDate
        ? `── Expected vs Actual (${forecast.forecastFor}) ──`
        : `── Forecast for ${forecast.forecastFor} ──`;
      lines.push(
        ``,
        header,
        `Forecast Range : ${f(forecast.forecastRange)}${diff(forecast.forecastRange)}`,
        ``,
        `ℹ  ± diff = forecast − actual range`,
      );
    }

    return lines.join('\n');
  }

  private formatHistorical(row: RangeEstimationRow): string {
    const fmt = (v: number | undefined) => v != null ? v.toFixed(2) : 'N/A';
    return [
      `${row.ticker} — ${row.date}`,
      `Range : ${fmt(row.range)}`,
      `High  : ${fmt(row.high)}`,
      `Low   : ${fmt(row.low)}`,
      `Close : ${fmt(row.currentClose)}`,
    ].join('\n');
  }
}
